import {useMutation, UseMutationResult} from '@tanstack/react-query';
import {useState} from 'react';
import {useLocale} from 'state/locale';
import {c} from 'ttag';
import Api from 'types/api';
import {DocumentType} from 'types/documents';
import {toSnakeCase} from 'utils/object';
import {ContentType} from 'utils/request';
import {ProgressEvent, request} from 'utils/request-axios';

import {DxFormData} from '../types';
import {DocumentReminderSettings} from './DocumentReminders/types';
import {UploadProgress} from './DXHelper/utils';

type PossibleResponses = Api.Response.SuccessAxios;

const useSendingSettings = (): UseMutationResult<
    PossibleResponses,
    Error,
    unknown
> => {
    const locale = useLocale();

    return useMutation({
        mutationFn: (formData) =>
            request({
                formData: toSnakeCase(formData),
                locale,
                method: 'POST',
                url: window.Urls.partner_sending_settings_view(),
            }),
    });
};

export default useSendingSettings;

export const useSetEmailSettings = (): UseMutationResult<
    PossibleResponses,
    Error,
    {contact_mid: null | string; sender_names: {en: string; ja: string}}
> => {
    const locale = useLocale();

    return useMutation({
        mutationFn: (formData) =>
            request({
                formData: toSnakeCase(formData),
                locale,
                method: 'POST',
                url: window.Urls.partner_set_email_settings_view(),
            }),
    });
};

export const useSetPartnerDxSettings = (): UseMutationResult<
    PossibleResponses,
    Error,
    unknown
> => {
    const locale = useLocale();
    const partnerMid = window.HatsConstants.partner;

    return useMutation({
        mutationFn: (formData) => {
            return request({
                contentType: ContentType.JSON,
                formData: toSnakeCase(formData),
                locale,
                method: 'POST',
                url: window.Urls.api_dx_partner_settings(partnerMid),
            });
        },
    });
};

type UploadDxParams = {
    file: File;
    progressCallback?: (progress: ProgressEvent) => void;
};

export const useUploadDxInstructions = (): UseMutationResult<
    PossibleResponses,
    Error,
    UploadDxParams
> => {
    const locale = useLocale();
    const partnerMid = window.HatsConstants.partner;
    return useMutation({
        mutationFn: () => {
            return request({
                contentType: ContentType.JSON,
                formData: {},
                locale,
                method: 'POST',
                url: window.Urls.api_dx_partner_settings_instructions_upload_ticket(
                    partnerMid,
                ),
            });
        },
        onSuccess: (response, {file, progressCallback}: UploadDxParams) => {
            const ticketUrl = response.upload_url;
            const formData = new FormData();
            formData.append('file', file);
            return request({
                contentType: ContentType.FORM_DATA,
                formData: formData,
                locale,
                method: 'POST',
                onUploadProgress: (progress: ProgressEvent) =>
                    progressCallback && progressCallback(progress),
                url: ticketUrl,
            });
        },
    });
};

export const useUpdateDxInstructions = (): UseMutationResult<
    PossibleResponses,
    Error,
    null | string
> => {
    const locale = useLocale();
    const partnerMid = window.HatsConstants.partner;
    return useMutation({
        mutationFn: (partnerfile: null | string) =>
            request({
                contentType: ContentType.JSON,
                formData: {partnerfile},
                locale,
                method: 'POST',
                url: window.Urls.api_dx_partner_settings_instructions(
                    partnerMid,
                ),
            }),
    });
};

type FormDataProcessed = {
    target: 'clientinbox_user' | 'default_contact';
} & Omit<DxFormData, 'target'>;
const processFormData = (data: DxFormData): FormDataProcessed => {
    const {...formData} = data;
    return {
        ...formData,
        target: data.target!,
    };
};

export const useSubmitDxForm = ({
    initialData,
    instructionsFile,
}: {
    initialData: DxFormData;
    instructionsFile: File | null;
}) => {
    const [instructionsProgress, setInstructionsProgress] =
        useState<null | UploadProgress>(null);

    const api = useSetPartnerDxSettings();
    const uploadInstructions = useUploadDxInstructions();
    const updateInstructions = useUpdateDxInstructions();

    const handleSubmit = async (formData: DxFormData) => {
        const jsonFormData = {...processFormData(formData)};
        const hadInstructions = initialData.instructions !== null;
        const {instructions} = jsonFormData;
        delete jsonFormData['instructions'];

        const progressCallback = (progress: ProgressEvent) => {
            if (!instructionsFile) {
                setInstructionsProgress(null);
                return;
            }
            setInstructionsProgress({
                bytesTotal: instructionsFile.size,
                bytesTransferred: progress.loaded,
                filename: instructionsFile.name,
                state: 'uploading',
            });
        };

        if (instructionsFile) {
            setInstructionsProgress({
                bytesTotal: instructionsFile.size,
                bytesTransferred: 0,
                filename: instructionsFile.name,
                state: 'uploading',
            });
            const uploadResult = await uploadInstructions
                .mutateAsync({file: instructionsFile, progressCallback})
                .then(() => {
                    setInstructionsProgress({
                        bytesTotal: instructionsFile.size,
                        bytesTransferred: instructionsFile.size,
                        filename: instructionsFile.name,
                        state: 'done',
                    });
                    return true;
                })
                .catch((errResponse) => {
                    const responseErrors =
                        (errResponse.response &&
                            errResponse.response.data &&
                            errResponse.response.data.response &&
                            errResponse.response.data.response.errors) ||
                        [];
                    setInstructionsProgress({
                        filename: instructionsFile.name,
                        message:
                            responseErrors.length > 0
                                ? responseErrors[0].message
                                : c('dx-helper')
                                      .t`There was an error uploading the file -- please check the PDF and try again.`,
                        state: 'error',
                    });
                    return false;
                });
            if (uploadResult === false) {
                throw {
                    error: {
                        response: {
                            data: {
                                response: {
                                    errors: {
                                        instructionsFile: [
                                            [
                                                c('dx-helper')
                                                    .t`There was an error uploading the file -- please check the PDF and try again.`,
                                            ],
                                        ],
                                    },
                                },
                            },
                        },
                    },
                };
            }
        } else if (hadInstructions && !instructions) {
            await updateInstructions.mutateAsync(null);
        }

        await api.mutateAsync(processFormData(jsonFormData));
    };

    return {
        handleSubmit,
        instructionsProgress,
        setInstructionsProgress,
    };
};

export type UpdateDocumentReminderSettingsErrorResponse = Api.Response.Failure<{
    [K in DocumentType]: boolean;
}>;

export type UpdateDocumentReminderSettingsRequest = DocumentReminderSettings;

export const useUpdateDocumentReminderSettings = (): UseMutationResult<
    Api.Response.Success<Api.Response.Template.SuccessBasicMessage>,
    UpdateDocumentReminderSettingsErrorResponse,
    UpdateDocumentReminderSettingsRequest
> => {
    const locale = useLocale();

    return useMutation({
        mutationFn: (formData) =>
            request({
                formData: toSnakeCase(formData),
                locale,
                method: 'POST',
                url: window.Urls.partner_document_reminder_settings_edit_view(),
            }),
    });
};
